import type { AxiosError, AxiosResponse } from "axios";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import type { PaginationRequest } from "@/shared/types";
import { apiInstance } from "@/shared/api";
import { useMutate } from "@/shared/hooks";
import { filterObjectKeys } from "@/shared/lib/utils";
import type { TableApiCallParams } from "@/shared/types";
import type {
  AssignCuratorRequest,
  AssignCuratorToTeamleadRequest,
  ContactUrlRequest,
  CreateCuratorRequest,
  CuratorActivationRequest,
  DeleteCuratorRequest,
} from "./api.dto";
import { useToastNotifications } from "@/shared/hooks/useToastNotifications";
import type { PaginatedResponse } from "@/shared/types";
import type { Curator } from "../model";

export const useMyCurators = (
  params: TableApiCallParams & { enabled: boolean },
) => {
  return useQuery<PaginatedResponse<Curator>>({
    queryKey: ["curatorsMy", filterObjectKeys(params, ["enabled"])],
    queryFn: async () => {
      const { data } = await apiInstance.get<PaginatedResponse<Curator>>(
        "/curators/my",
        filterObjectKeys(params, ["enabled"]),
      );

      return data;
    },
    enabled: params.enabled,
  });
};

export const useMyCuratorsInf = (
  params: Omit<PaginationRequest, "currentPage"> & { enabled?: boolean },
) => {
  return useInfiniteQuery<PaginatedResponse<Curator>>({
    queryKey: ["useCurators", filterObjectKeys(params, ["enabled"])],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await apiInstance.get<PaginatedResponse<Curator>>(
        "/curators/my",
        {
          ...filterObjectKeys(params, ["enabled"]),
          currentPage: pageParam,
        },
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.total;
      const itemsPerPage = 5;
      const currentPage = allPages.length;

      const totalPages = Math.ceil(totalItems / itemsPerPage);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    enabled: params.enabled,
  });
};

export const useCurators = (params: TableApiCallParams) => {
  return useQuery<PaginatedResponse<Curator>>({
    queryKey: ["curators", filterObjectKeys(params)],
    queryFn: async () => {
      const { data } = await apiInstance.get<PaginatedResponse<Curator>>(
        "/curators",
        filterObjectKeys(params),
      );
      return data;
    },
  });
};

//TODO: зарефакторить dto
export const useCuratorsInf = (
  params: Omit<TableApiCallParams & { isActive?: boolean }, "currentPage">,
) => {
  return useInfiniteQuery({
    queryKey: ["curatorfInf", filterObjectKeys(params)],
    queryFn: async ({ pageParam }) => {
      const response = await apiInstance.get<PaginatedResponse<Curator>>(
        "/curators",
        {
          ...filterObjectKeys(params),
          currentPage: pageParam,
        },
      );

      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.total;
      const itemsPerPage = 5;
      const currentPage = allPages.length;

      const totalPages = Math.ceil(totalItems / itemsPerPage);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });
};

export const useAssignCurator = () => {
  const { mutationFn, onError } = useMutate({
    method: "post",
    url: "/curators/assign",
    toast: {
      success: {
        title: "Куратор назначен",
      },
    },
  });
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse, AxiosError, AssignCuratorRequest>({
    mutationKey: ["assignCurator"],
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["managers"] });
    },
    onError,
  });
};

export const useUnassignCurator = () => {
  const { mutationFn, onError } = useMutate({
    method: "patch",
    url: "/curators/unassign",
    toast: {
      success: {
        title: "Куратор снят.",
      },
    },
  });
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse, AxiosError, AssignCuratorRequest>({
    mutationKey: ["unassignCurator"],
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["managers"] });
    },
    onError,
  });
};

export const useCreateCurator = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "post",
    url: "/curators",
    toast: {
      success: {
        title: "Куратор создан.",
      },
    },
    refetchQueries: ["curators", "curatorsMy"],
  });

  return useMutation<AxiosResponse, AxiosError, CreateCuratorRequest>({
    mutationKey: ["createCurator"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useDeleteCurator = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        title: "Куратор удален.",
      },
    },
  });
  return useMutation<AxiosResponse, AxiosError, DeleteCuratorRequest, Response>(
    {
      mutationKey: ["deleteCurator"],
      mutationFn: ({ id }) => {
        return apiInstance.delete(`/curators/${id}`);
      },
      onSuccess,
      onError,
    },
  );
};

export const useCuratorActivaton = () => {
  const { onSuccess, onError } = useToastNotifications({
    refetchQueries: ["curators", "curatorsMy"],
  });
  return useMutation<
    AxiosResponse,
    AxiosError,
    CuratorActivationRequest,
    Response
  >({
    mutationKey: ["activateCurator"],
    mutationFn: ({ id, status }) => {
      return apiInstance.patch(`/curators/${id}/status`, {
        status,
      });
    },
    onSuccess,
    onError,
  });
};

export const useAssignCuratorToTeamlead = () => {
  const { onError } = useToastNotifications({
    refetchQueries: ["curators", "curatorsMy"],
    toast: {
      success: {
        description: "Куратор назначен.",
      },
    },
  });
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse, AxiosError, AssignCuratorToTeamleadRequest>(
    {
      mutationKey: ["assignCuratorToTeamlead"],
      mutationFn: ({ curatorId, teamleadId }) => {
        return apiInstance.post(
          `/curators/${curatorId}/teamlead/${teamleadId}`,
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["curators"] });
        queryClient.invalidateQueries({ queryKey: ["curatorsMy"] });
      },
      onError,
    },
  );
};

export const useUnassignCuratorFromTeamlead = () => {
  const { onSuccess, onError } = useToastNotifications({
    refetchQueries: ["curators"],
    toast: {
      success: {
        description: "Куратор отвязан.",
      },
    },
  });

  return useMutation<
    AxiosResponse,
    AxiosError,
    Pick<AssignCuratorToTeamleadRequest, "curatorId">,
    Response
  >({
    mutationKey: ["assignCuratorToTeamlead"],
    mutationFn: ({ curatorId }) => {
      return apiInstance.delete(`/curators/${curatorId}/teamlead`);
    },
    onSuccess,
    onError,
  });
};

export const useSetContactUrl = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["curatorCondactUrl"],
    mutationFn: async (data: ContactUrlRequest) =>
      await apiInstance.patch(`/curators/${data.curatorId}/contact-url`, {
        contactUrl: data.contactUrl,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["curators"] }),
        queryClient.invalidateQueries({ queryKey: ["useCurators"] }),
        queryClient.invalidateQueries({ queryKey: ["curatorsMy"] }),
      ]);
    },
  });
};
